// GA4 page_view：每次 AR viewer 被打開，以「物件」為單位送一次 page_view（AM-169）。
//
// 時機：
// - 正常情況在 getProductInfo 有結果（成功或失敗）時送，帶 object_id + object_type。
//   這支 API 是 App 一啟動就打的，多數情況比 window load 早回來，所以會比改版前（load 後才送）早。
// - 保底：window load 後再等 FALLBACK_DELAY_AFTER_LOAD_MS，API 仍沒結果（例如卡住）就送只帶 object_id 的版本。
//   不在 load 當下就送，是因為 load 與 API 回應常常只差幾十毫秒（快取命中時 load 甚至更早），
//   立刻送會讓大部分 page_view 掉了 object_type。
// - 不論哪條路徑先到，一次頁面載入只送一次（切換 modelIndex、語系都不會重送）。
//
// visit_id（AM-185）：本次頁面載入的 id，與送給後端 online_visit_log 的 visitId 相同，讓兩邊對得起來。見 utils/visitor。
// （AM-182 的 object_visitor_type 已移除，新舊訪客改由後端依 visitor id 判斷。）
//
// 頻寬：gtag 指令先排進 dataLayer，gtag.js 等到要送 page_view 時才載入，
// 並以 async + fetchpriority=low 載入，不跟模型等關鍵資源搶頻寬。
// 事件只有在 gtag.js 執行後才會真正送出，所以腳本不能一路延到 load 之後才載，否則等於沒提早。

const GTAG_SRC = "https://www.googletagmanager.com/gtag/js?id=";

// 對應 am-be-api 的 ProjectType
const OBJECT_TYPES = ["ARMAKER", "GS", "PG", "SCANKIT"];

export const FALLBACK_DELAY_AFTER_LOAD_MS = 2000;

// 與 App 呼叫 /publish/v1/product/object/{uid} 用的值一致（URLSearchParams 解一次、再 decodeURIComponent 一次），
// 但 uid 缺失時回傳 undefined，而不是 decodeURIComponent(null) 產生的字串 "null"。
export const getObjectId = (search) => {
  const raw = new URLSearchParams(search).get("uid");
  if (!raw) return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

// object_type 取自 getProductInfo 回傳的 onlineProduct.type；不認得或沒有就不帶。
export const getObjectType = (productInfo) => {
  const type = productInfo?.onlineProduct?.type;
  if (typeof type !== "string") return undefined;
  const normalized = type.toUpperCase();
  return OBJECT_TYPES.includes(normalized) ? normalized : undefined;
};

export const createPageViewTracker = ({ measurementId, objectId, visitId, win = window }) => {
  let sent = false;

  const loadGtagScript = () => {
    const script = win.document.createElement("script");
    script.async = true;
    script.setAttribute("fetchpriority", "low");
    script.src = `${GTAG_SRC}${measurementId}`;
    win.document.head.appendChild(script);
  };

  const send = (objectType) => {
    if (sent || !measurementId) return;
    sent = true;

    win.dataLayer = win.dataLayer || [];
    // gtag.js 只認 arguments 物件，不能改成 rest 參數陣列
    win.gtag =
      win.gtag ||
      function gtag() {
        // eslint-disable-next-line prefer-rest-params
        win.dataLayer.push(arguments);
      };

    win.gtag("js", new Date());
    win.gtag("config", measurementId, {
      // page_view 由這裡手動送，關掉 config 自動送的那一次
      send_page_view: false,
      // viewer 常被跨站 iframe 嵌入，_ga cookie 需 SameSite=None 才能在 iframe 內保留
      cookie_flags: "SameSite=None;Secure",
    });
    win.gtag("event", "page_view", {
      ...(objectId && { object_id: objectId }),
      ...(objectType && { object_type: objectType }),
      ...(visitId && { visit_id: visitId }),
    });

    loadGtagScript();
  };

  const sendFallbackAfterLoad = () => {
    const startTimer = () => win.setTimeout(() => send(), FALLBACK_DELAY_AFTER_LOAD_MS);
    if (win.document.readyState === "complete") {
      startTimer();
    } else {
      win.addEventListener("load", startTimer, { once: true });
    }
  };

  return { send, sendFallbackAfterLoad };
};
